/**
 * Searchable Asset Picker.
 *
 * Typeahead combobox for finding and selecting assets to assign to a
 * yard section. Searches by SKU, name, serial number, VIN, make, or
 * model with debounced API calls. Shows rich result cards with status
 * badges and current location.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Search, Package, MapPin, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import Badge from "@/components/ui/Badge";

interface AssetResult {
  id: string;
  sku: string;
  name: string;
  category: string;
  status: string;
  location: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  yard_section_id: string | null;
}

interface AssetPickerProps {
  /** IDs of assets already in the current section (to filter out). */
  excludeAssetIds?: string[];
  /** Called when the user selects an asset. */
  onSelect: (assetId: string) => void;
  /** Placeholder text. */
  placeholder?: string;
  /** Whether the picker is disabled. */
  disabled?: boolean;
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const statusVariant = (
  status: string,
): "success" | "warning" | "danger" | "default" => {
  if (status === "Received" || status === "Available") return "success";
  if (status === "In Service" || status === "Reserved") return "warning";
  if (status === "Sold" || status === "Scrapped") return "danger";
  return "default";
};

function AssetPicker({
  excludeAssetIds = [],
  onSelect,
  placeholder = "Search by SKU, name, serial, VIN...",
  disabled = false,
}: AssetPickerProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const debouncedQuery = useDebounce(query, 300);

  const { data: results, isLoading } = useQuery<{ items: AssetResult[] }>({
    queryKey: ["asset-search", debouncedQuery],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/assets?search=${encodeURIComponent(debouncedQuery)}&page_size=8`,
        )
      ).data,
    enabled: debouncedQuery.length >= 2,
  });

  const excludeSet = new Set(excludeAssetIds);
  const filteredResults = (results?.items ?? []).filter(
    (a) => !excludeSet.has(a.id),
  );

  // Reset highlight when results change.
  useEffect(() => setHighlightIdx(0), [filteredResults.length]);

  const handleSelect = useCallback(
    (assetId: string) => {
      onSelect(assetId);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filteredResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filteredResults[highlightIdx];
      if (item) handleSelect(item.id);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setIsOpen(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  const yearMakeModel = (a: AssetResult) => {
    const parts = [a.year, a.make, a.model].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  };

  return (
    <div className="relative">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-tertiary)]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full h-9 pl-8 pr-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)]"
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && debouncedQuery.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--surface-base)] shadow-lg"
        >
          {isLoading && (
            <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
              Searching...
            </div>
          )}

          {!isLoading && filteredResults.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
              No assets found for "{debouncedQuery}"
            </div>
          )}

          {!isLoading &&
            filteredResults.map((asset, idx) => {
              const ymm = yearMakeModel(asset);
              return (
                <div
                  key={asset.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(asset.id)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer border-b border-[var(--color-border)] last:border-0 transition-colors ${
                    idx === highlightIdx
                      ? "bg-brand-50 dark:bg-brand-900/10"
                      : "hover:bg-[var(--surface-sunken)]"
                  }`}
                >
                  {/* Icon */}
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--surface-sunken)] mt-0.5">
                    <Package className="h-4 w-4 text-[var(--text-tertiary)]" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-brand-600 dark:text-brand-300">
                        {asset.sku}
                      </span>
                      <Badge variant={statusVariant(asset.status)}>
                        {asset.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[var(--text-primary)] truncate mt-0.5">
                      {asset.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-[var(--text-tertiary)]">
                        {asset.category}
                      </span>
                      {ymm && (
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          · {ymm}
                        </span>
                      )}
                    </div>
                    {asset.location && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin className="h-2.5 w-2.5 text-[var(--text-tertiary)]" />
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          {asset.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default AssetPicker;
